import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileCheck, Download, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { generateEstoppelWordDocument } from "@/utils/estoppelWordGenerator";

interface Deal {
  id: string;
  deal_title: string;
  seller_name: string;
  seller_email: string;
  buyer_name: string;
  buyer_address: string;
  property_address: string;
  current_balance: number;
  payment_amount: number;
  interest_rate: number;
  first_payment_date: string;
  buyer_last_payment_date: string;
  buyer_last_payment_amount: number;
  buyer_current_balance_stated: number;
  payments_remaining: number;
  has_balloon_payment: boolean;
  balloon_amount: number;
  balloon_due_date: string;
}

export function EstoppelCertificate() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  
  const [formData, setFormData] = useState({
    buyerName: "",
    buyerAddress: "",
    sellerName: "",
    investorName: "",
    investorAddress: "",
    propertyAddress: "",
    originalContractDate: "",
    currentBalance: "",
    monthlyPayment: "",
    interestRate: "",
    paymentsRemaining: "",
    lastPaymentDate: "",
    lastPaymentAmount: "",
    hasBalloon: false,
    balloonAmount: "",
    balloonDueDate: "",
    propertyTaxesCurrent: "Yes",
    insuranceCurrent: "Yes",
    propertyCondition: "Good",
    additionalNotes: "",
  });

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    const { data, error } = await supabase
      .from('deals')
      .select(`
        id, deal_title, seller_name, seller_email, buyer_name, buyer_address,
        property_address, current_balance, payment_amount, interest_rate,
        first_payment_date, buyer_last_payment_date, buyer_last_payment_amount,
        buyer_current_balance_stated, payments_remaining, has_balloon_payment,
        balloon_amount, balloon_due_date
      `)
      .order('deal_title');
    
    if (error) {
      console.error('Error fetching deals:', error);
      toast.error("Failed to load deals");
      return;
    }
    
    setDeals(data || []);
  };

  const handleDealSelect = (dealId: string) => {
    setSelectedDealId(dealId);
    const deal = deals.find(d => d.id === dealId);
    
    if (deal) {
      setFormData({
        buyerName: deal.buyer_name || "",
        buyerAddress: deal.buyer_address || "",
        sellerName: deal.seller_name || "",
        investorName: "",
        investorAddress: "",
        propertyAddress: deal.property_address || "",
        originalContractDate: deal.first_payment_date || "",
        currentBalance: deal.buyer_current_balance_stated?.toString() || deal.current_balance?.toString() || "",
        monthlyPayment: deal.payment_amount?.toString() || "",
        interestRate: deal.interest_rate?.toString() || "",
        paymentsRemaining: deal.payments_remaining?.toString() || "",
        lastPaymentDate: deal.buyer_last_payment_date || "",
        lastPaymentAmount: deal.buyer_last_payment_amount?.toString() || "",
        hasBalloon: deal.has_balloon_payment || false,
        balloonAmount: deal.balloon_amount?.toString() || "",
        balloonDueDate: deal.balloon_due_date || "",
        propertyTaxesCurrent: "Yes",
        insuranceCurrent: "Yes",
        propertyCondition: "Good",
        additionalNotes: "",
      });
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateDocument = async () => {
    if (!formData.buyerName || !formData.propertyAddress) {
      toast.error("Please fill in buyer name and property address");
      return;
    }

    setIsGenerating(true);
    try {
      const blob = await generateEstoppelWordDocument(formData);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Estoppel_Certificate_${formData.buyerName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Estoppel certificate generated successfully!");
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error("Failed to generate document");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendEmail = async () => {
    const deal = deals.find(d => d.id === selectedDealId);
    if (!deal?.seller_email) {
      toast.error("No email address available for this deal");
      return;
    }

    toast.info("Email functionality coming soon!");
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCheck className="h-5 w-5" />
            Estoppel Certificate Generator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">About Estoppel Certificates:</h4>
            <p className="text-sm text-blue-700">
              An estoppel certificate is a signed statement by the buyer confirming the current status of 
              the land contract, including balance owed, payment terms, and property condition. This document 
              is essential for due diligence before purchasing a contract.
            </p>
          </div>

          <div>
            <Label htmlFor="deal-selector">Load Deal Data (Optional)</Label>
            <Select value={selectedDealId} onValueChange={handleDealSelect}>
              <SelectTrigger id="deal-selector">
                <SelectValue placeholder="Select a deal to auto-fill data..." />
              </SelectTrigger>
              <SelectContent>
                {deals.map((deal) => (
                  <SelectItem key={deal.id} value={deal.id}>
                    {deal.deal_title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="buyerName">Buyer Name *</Label>
              <Input
                id="buyerName"
                value={formData.buyerName}
                onChange={(e) => handleInputChange('buyerName', e.target.value)}
                placeholder="John and Jane Doe"
              />
            </div>
            <div>
              <Label htmlFor="sellerName">Seller Name</Label>
              <Input
                id="sellerName"
                value={formData.sellerName}
                onChange={(e) => handleInputChange('sellerName', e.target.value)}
                placeholder="Seller Name"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="investorName">Investor/Assignee Name</Label>
              <Input
                id="investorName"
                value={formData.investorName}
                onChange={(e) => handleInputChange('investorName', e.target.value)}
                placeholder="Investor or company name"
              />
            </div>
            <div>
              <Label htmlFor="investorAddress">Investor/Assignee Address</Label>
              <Input
                id="investorAddress"
                value={formData.investorAddress}
                onChange={(e) => handleInputChange('investorAddress', e.target.value)}
                placeholder="123 Investor St, City, State ZIP"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="buyerAddress">Buyer Address</Label>
            <Input
              id="buyerAddress"
              value={formData.buyerAddress}
              onChange={(e) => handleInputChange('buyerAddress', e.target.value)}
              placeholder="123 Main St, City, State ZIP"
            />
          </div>

          <div>
            <Label htmlFor="propertyAddress">Property Address *</Label>
            <Input
              id="propertyAddress"
              value={formData.propertyAddress}
              onChange={(e) => handleInputChange('propertyAddress', e.target.value)}
              placeholder="456 Property Ave, City, State ZIP"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="currentBalance">Current Balance</Label>
              <Input
                id="currentBalance"
                type="number"
                step="0.01"
                value={formData.currentBalance}
                onChange={(e) => handleInputChange('currentBalance', e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="monthlyPayment">Monthly Payment</Label>
              <Input
                id="monthlyPayment"
                type="number"
                step="0.01"
                value={formData.monthlyPayment}
                onChange={(e) => handleInputChange('monthlyPayment', e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="interestRate">Interest Rate (%)</Label>
              <Input
                id="interestRate"
                type="number"
                step="0.01"
                value={formData.interestRate}
                onChange={(e) => handleInputChange('interestRate', e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="paymentsRemaining">Payments Remaining</Label>
              <Input
                id="paymentsRemaining"
                type="number"
                value={formData.paymentsRemaining}
                onChange={(e) => handleInputChange('paymentsRemaining', e.target.value)}
                placeholder="0"
              />
            </div>
            <div>
              <Label htmlFor="lastPaymentDate">Last Payment Date</Label>
              <Input
                id="lastPaymentDate"
                type="date"
                value={formData.lastPaymentDate}
                onChange={(e) => handleInputChange('lastPaymentDate', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="lastPaymentAmount">Last Payment Amount</Label>
              <Input
                id="lastPaymentAmount"
                type="number"
                step="0.01"
                value={formData.lastPaymentAmount}
                onChange={(e) => handleInputChange('lastPaymentAmount', e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          {formData.hasBalloon && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="balloonAmount">Balloon Payment Amount</Label>
                <Input
                  id="balloonAmount"
                  type="number"
                  step="0.01"
                  value={formData.balloonAmount}
                  onChange={(e) => handleInputChange('balloonAmount', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label htmlFor="balloonDueDate">Balloon Due Date</Label>
                <Input
                  id="balloonDueDate"
                  type="date"
                  value={formData.balloonDueDate}
                  onChange={(e) => handleInputChange('balloonDueDate', e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="propertyTaxesCurrent">Property Taxes Current?</Label>
              <Select 
                value={formData.propertyTaxesCurrent} 
                onValueChange={(value) => handleInputChange('propertyTaxesCurrent', value)}
              >
                <SelectTrigger id="propertyTaxesCurrent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                  <SelectItem value="Unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="insuranceCurrent">Insurance Current?</Label>
              <Select 
                value={formData.insuranceCurrent} 
                onValueChange={(value) => handleInputChange('insuranceCurrent', value)}
              >
                <SelectTrigger id="insuranceCurrent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                  <SelectItem value="Unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="propertyCondition">Property Condition</Label>
              <Select 
                value={formData.propertyCondition} 
                onValueChange={(value) => handleInputChange('propertyCondition', value)}
              >
                <SelectTrigger id="propertyCondition">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Excellent">Excellent</SelectItem>
                  <SelectItem value="Good">Good</SelectItem>
                  <SelectItem value="Fair">Fair</SelectItem>
                  <SelectItem value="Poor">Poor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="additionalNotes">Additional Notes</Label>
            <Textarea
              id="additionalNotes"
              value={formData.additionalNotes}
              onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
              placeholder="Any additional information or special conditions..."
              rows={4}
            />
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={handleGenerateDocument}
              disabled={isGenerating}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {isGenerating ? "Generating..." : "Generate Document"}
            </Button>
            {selectedDealId && (
              <Button 
                onClick={handleSendEmail}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Mail className="h-4 w-4" />
                Email to Seller
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
